import React, { useState } from 'react';
import { View, Pressable } from 'react-native';
import THEME from '../../../config/theme';
import { FontAwesome } from '@expo/vector-icons';
import {
  ViewComment,
  CommentBody,
  CommentHeader,
  RepliesGroup,
  ThreadLine,
} from '../style';
import ReplyItem from '../ReplyItem';
import { TouchableOpacity } from 'react-native-gesture-handler';
import { SmallText, FooterText } from '../../../config/theme/globalStyles';
import { usePublicUsers, useUser } from '../../../queries/userQuery';
import { useCustomTheme } from '../../../contexts/useCustomTheme';
import { hexToRgba } from '../../../utils/colorUtils';
import { timeAgo } from '../../../utils/dateUtils';
import CommentAvatar from '../CommentAvatar';
import { useCommentTypography } from '../useCommentTypography';

// Quantas respostas aparecem antes do "Ver mais N respostas". Numa coluna de
// 30% da tela, uma thread longa aberta empurra todos os outros comentários
// para fora do campo de visão.
const VISIBLE_REPLIES = 2;

const CommentItem = ({
  login,
  comment,
  editPlaceholder,
  community,
  handleDeleteComment,
  handleDeleteReply,
  getDataReply,
  changeReply,
  idPost,
  isAdminOrIsUserCurrent,
  userId,
  handleDeleteReplyCard,
}) => {
  const [showAllReplies, setShowAllReplies] = useState(false);

  const { customColor } = useCustomTheme();
  const { data: userData } = useUser();

  // Cacheado e deduplicado por uid pelo react-query: vários comentários do
  // mesmo autor resolvem numa leitura só, e a lista não refaz nada ao
  // re-renderizar. Serve de fallback para comentários antigos, gravados antes
  // de o autor passar a ser salvo junto do comentário.
  const authorsById = usePublicUsers([comment?.userId]);
  const author = authorsById.get(comment?.userId);

  const authorName =
    comment?.authorName || comment?.userName || author?.name || 'Aluno';
  const authorPhoto = comment?.authorPhoto || author?.photo || null;
  const authorIsAdmin =
    comment?.authorIsAdmin === true || author?.isAdmin === true;

  const textColor = customColor?.text?.app || THEME.COLORS.TEXT_MAIN;
  const accent = customColor?.primary || THEME.COLORS.PRIMARY_900;
  const divider = hexToRgba(textColor, 0.08) || '#E5E7EB';
  const muted = hexToRgba(textColor, 0.6) || '#6B7280';

  const { fontTitle, fontText, fontSM, fontXS, lineHeight } =
    useCommentTypography();

  // Recalculado a cada render (antes era congelado no mount, então "há 2
  // minutos" continuava dizendo isso uma hora depois). `null` enquanto o
  // serverTimestamp não resolve — o eco local chega sem data.
  const createdAt = timeAgo(comment?.createdAt) || 'agora';

  const replies = comment?.replies || [];
  const hasReplies = replies.length > 0;
  const visibleReplies =
    showAllReplies || replies.length <= VISIBLE_REPLIES
      ? replies
      : replies.slice(0, VISIBLE_REPLIES);
  const hiddenRepliesCount = replies.length - visibleReplies.length;

  // Dois contratos diferentes: na Community o dono do post é resolvido só pelo
  // autor do comentário; aqui entram o usuário atual e a flag de admin.
  const canDelete =
    typeof isAdminOrIsUserCurrent === 'function' &&
    (idPost
      ? isAdminOrIsUserCurrent(comment?.userId)
      : isAdminOrIsUserCurrent(userId, userData?.isAdmin, comment?.userId));

  const onReplyPress = () => {
    if (community) {
      getDataReply?.(comment);
      changeReply?.(true);
      return;
    }
    editPlaceholder?.(`Responder ${authorName}`, comment.id, authorName);
  };

  return (
    // A linha SEMPRE renderiza. Antes o componente devolvia `false` enquanto
    // buscava o autor — e como o autor era buscado de novo a cada remontagem,
    // os comentários sumiam e voltavam. Pior: se o documento do autor não
    // existisse ou as regras negassem a leitura, o comentário nunca aparecia.
    <View style={{ position: 'relative' }}>
      <ViewComment>
        <CommentAvatar
          size={36}
          photo={authorPhoto}
          isAdmin={authorIsAdmin}
          label={`Foto de ${authorName}`}
        />

        <CommentBody style={{ marginLeft: 12 }}>
          <CommentHeader>
            <View
              style={{
                flexDirection: 'row',
                alignItems: 'center',
                flex: 1,
                minWidth: 0,
              }}
            >
              <SmallText
                color={textColor}
                textAlign="left"
                numberOfLines={1}
                style={{ fontFamily: fontTitle, fontSize: fontSM, flexShrink: 1 }}
              >
                {authorName}
              </SmallText>
              <FooterText
                color={muted}
                style={{ fontFamily: fontText, fontSize: fontXS, marginLeft: 6 }}
              >
                {`· ${createdAt}`}
              </FooterText>
            </View>

            {canDelete && (
              <TouchableOpacity
                onPress={() =>
                  idPost
                    ? handleDeleteComment(comment.id, idPost)
                    : handleDeleteComment(comment.id)
                }
                hitSlop={12}
                accessibilityRole="button"
                accessibilityLabel={`Excluir comentário de ${authorName}`}
                style={{ paddingLeft: 8, cursor: 'pointer' }}
              >
                <FontAwesome name="trash-o" size={16} color={muted} />
              </TouchableOpacity>
            )}
          </CommentHeader>

          <SmallText
            color={textColor}
            textAlign="left"
            style={{
              fontFamily: fontText,
              fontSize: fontSM,
              lineHeight,
              marginTop: 2,
            }}
          >
            {comment?.comment}
          </SmallText>

          {/* Na aula o botão depende de haver usuário logado; na Community ele
              sempre apareceu, e mudar isso aqui alteraria uma tela que não é o
              alvo desta correção. */}
          {(community || login) && (
            <Pressable
              onPress={onReplyPress}
              accessibilityRole="button"
              accessibilityLabel={`Responder ${authorName}`}
              style={{ alignSelf: 'flex-start', paddingVertical: 4, paddingRight: 8 }}
            >
              <FooterText
                color={accent}
                textAlign="left"
                style={{ fontFamily: fontTitle, fontSize: fontXS, marginTop: 2 }}
              >
                Responder
              </FooterText>
            </Pressable>
          )}
        </CommentBody>
      </ViewComment>

      {hasReplies && (
        <>
          <ThreadLine style={{ backgroundColor: divider }} />
          <RepliesGroup>
            {/* Sem FlatList: uma lista com `scrollEnabled={false}` continua
                sendo um ScrollView, e o react-native-web marca esse nó com
                `touch-action: none` — arrastar o dedo sobre as respostas
                travava a rolagem da página inteira no celular.
                A `key` é o id: como as respostas vêm mais-nova-primeiro, com
                índice uma resposta nova deslocava todas e as linhas passavam a
                exibir o autor errado. */}
            {visibleReplies.map((item) => (
              <ReplyItem
                key={item.id}
                reply={item}
                handleDeleteReply={
                  idPost ? handleDeleteReply : handleDeleteReplyCard
                }
                comment={comment}
                user={login}
              />
            ))}

            {hiddenRepliesCount > 0 && (
              <Pressable
                onPress={() => setShowAllReplies(true)}
                accessibilityRole="button"
                style={{ alignSelf: 'flex-start', paddingVertical: 4 }}
              >
                <FooterText
                  color={accent}
                  textAlign="left"
                  style={{ fontFamily: fontTitle, fontSize: fontXS }}
                >
                  {`Ver mais ${hiddenRepliesCount} ${
                    hiddenRepliesCount === 1 ? 'resposta' : 'respostas'
                  }`}
                </FooterText>
              </Pressable>
            )}
          </RepliesGroup>
        </>
      )}

      <View style={{ height: 1, backgroundColor: divider, marginLeft: 48 }} />
    </View>
  );
};

// A lista inteira é recriada a cada snapshot do Firestore; sem isto todo
// comentário re-renderiza quando qualquer um deles muda.
export default React.memo(CommentItem);
